import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormGroup, FormControl, FormArray, ReactiveFormsModule } from '@angular/forms';

import { Observable, combineLatest, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';

import { ValuesService } from '@app/common/values/values.service';

import { Attach, C_value } from '@app/shared/_models/common';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { AlertService } from '@app/shared/alert.service';
import { AttachService } from '@app/shared/attach/attach.service';
import { SmartSelectComponent } from '@app/shared/smart-select/smart-select.component';

import { ObjectType, C_VALUES_DELIVERY_OPERATOR, C_VALUES_DELIVERY_SLOT, C_VALUES_DELIVERY_MODE } from "@app/shared/constants";
import { LogisticParamsService } from '@app/shared/logistic-params.service';

@Component({
	selector: 'app-planificationdelivery',
	templateUrl: './planificationdelivery.component.html',
	styleUrl: './planificationdelivery.component.scss',
	imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule,  MatButtonToggleModule, MatCheckboxModule, SmartSelectComponent, SelectDateComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class PlanificationdeliveryComponent implements OnInit, OnDestroy {

	private loadOrderEventsSubscription!: Subscription;
	private formGroupChangeSubscription!: Subscription;
	@Input() loadOrderEventsSubject$!: Observable<FormGroup>;								// Parent order was loaded
	@Input() locked: boolean = false;										// Livraison signée => planification non modifiable

	@Output() changed = new EventEmitter<void>();

	public slotsDay: C_value[] = []; // Slot listbox content
	public slotsMorning: C_value[] = []; // Slot listbox content
	public slotsAfternoon: C_value[] = []; // Slot listbox content
	public operatorsL1: C_value[] = []; // Operator listbox content (précision 1)
	public operatorsL2: C_value[] = []; // Operator listbox content (précision 2)
	public deliverymodes: C_value[] = []; 		// Delivery modes (available from c_values 10)

	public formGroup: FormGroup = new FormGroup({});
	public mindate: Date = new Date(Date.now());							// TODO change it for existing deliveries, defaults to today

	public precisionControl1: FormControl = new FormControl();
	public precisionControl2: FormControl = new FormControl();
	public niveauPrecisionhoraire: number = 0;
	public askslotDayValue: string = '';
	public compareSlotById = (o1: any, o2: any) => String(o1) === String(o2);
	public errorMessage: string = '';

	// --- Attachments ---
	public attachs_images: Attach[] = [];

	private logisticParams = inject(LogisticParamsService);
	private valuesService = inject(ValuesService);
	private alertService = inject(AlertService);
	private attachService = inject(AttachService);

	ngOnInit(): void {
		combineLatest({
			operators: this.valuesService.getValues$(C_VALUES_DELIVERY_OPERATOR),
			slots:     this.valuesService.getValues$(C_VALUES_DELIVERY_SLOT),
			modes:     this.valuesService.getValues$(C_VALUES_DELIVERY_MODE),
		}).pipe(take(1)).subscribe({
			next: ({ operators, slots, modes }) => {

			operators.forEach((operator: any) => {
				if (operator.data.level === 1) this.operatorsL1.push(operator);
				if (operator.data.level === 2) this.operatorsL2.push(operator);
			});

			slots.forEach((slot: any) => {
				if (!slot.is_active) return;
				if (slot.data.parent === 0)      this.slotsDay.push(slot);
				else if (slot.data.parent === 1) this.slotsMorning.push(slot);
				else if (slot.data.parent === 2) this.slotsAfternoon.push(slot);
			});

			this.deliverymodes = modes;

			// Toutes les données sont prêtes : on peut écouter le parent
			this.loadOrderEventsSubscription = this.loadOrderEventsSubject$.subscribe((formgroup) => {
				if (!formgroup.get('id_order')) return;	// First subject is {}, ignored
				this.formGroup = formgroup;
				if (!this.formGroup.get("askdatdel")?.value) {
					this.formGroup.get('askdatdel')?.patchValue(new Date(), { emitEvent: false });
				}
				this.ensureDurationDefault();		// prérempli par le paramètre par défaut si non renseigné

				let slot = this.formGroup.get("askslot")?.value;
				if (!slot) {
					this.formGroup.get("askslot")?.setValue(0, { emitEvent: false });
					this.askslotDayValue = '';
				} else {
					// this.askslotDayValue = this.slotsMorning.find((e) => e.id == slot) != undefined ? "1" : "2";
					this.askslotDayValue = this.getSlotDayValue();
				}

				let op = this.formGroup.get("askop")?.value ?? 0;
				this.formGroup.get("askslot")?.patchValue((this.formGroup.get("askslot")?.value).toString(), { emitEvent: false });
				this.formGroup.get("askop")?.patchValue(op.toString(), { emitEvent: false });
				this.formGroup.get("mode")?.patchValue((this.formGroup.get("mode")?.value ?? 1).toString(), { emitEvent: false });

				if (this.operatorsL1.find((e) => e.id == op) != undefined) {
					this.niveauPrecisionhoraire = 1;
					this.precisionControl1.patchValue(true);
					this.precisionControl2.patchValue(false);
				} else if (this.operatorsL2.find((e) => e.id == op) != undefined) {
					this.niveauPrecisionhoraire = 2;
					this.precisionControl1.patchValue(true);
					this.precisionControl2.patchValue(true);
				 } else {
                        this.niveauPrecisionhoraire = 0;
                        this.precisionControl1.patchValue(false);
                        this.precisionControl2.patchValue(false);
                }

				if (this.formGroup?.value?.lnk_attachs) {
					const lnkAttachsFA = this.formGroup.get('lnk_attachs') as FormArray;
					const hasMemo = lnkAttachsFA.value.some((a: any) => a.data?.['flags']?.includes('is_default_memo'));
					if (!hasMemo) {
						lnkAttachsFA.push(this.attachService.getDefaultMemo(ObjectType.DELIVERY, this.formGroup.get('id')?.value ?? 0));
					}
					this.attachs_images = this.attachService.generate_attachs_object_with_thumbnail(this.formGroup.value.lnk_attachs);
				}
				this.is_valid();

				// (Re)souscription aux changements du formGroup de la livraison active :
				// toute modif de la planification (mode, date, créneau, opérateur, mémo…) prévient le parent.
				this.formGroupChangeSubscription?.unsubscribe();
				this.formGroupChangeSubscription = this.formGroup.valueChanges.subscribe(() => {
					this.changed.emit();
				});
			});
			},
			error: (e) => {
				this.alertService.error("ERREUR au chargement des paramètres de livraison");
				console.error(e);
			},
		});
	}

	onChangePrecision(event: MatCheckboxChange, i: number) {	// Check / uncheck precision 1 / precision 2
		this.niveauPrecisionhoraire = event.checked ? i : i - 1;
		if (!event.checked) {	// Uncheck box
			if (i === 1) {			// Uncheck precision 1 => retour au créneau Matin/Après-midi, opérateur effacé
				this.formGroup.get('askop')?.setValue(null);
				this.formGroup.get('askslot')?.setValue(this.askslotDayValue);
				this.precisionControl2.setValue(false);	// Uncheck precision 2
			} else {					// Uncheck precision 2 => efface l'opérateur horaire (>3) et le créneau horaire (>2)
				if ((this.formGroup.get('askop')?.value ?? 0) > 3) {
					this.formGroup.get('askop')?.setValue(null);
				}
				if ((this.formGroup.get('askslot')?.value ?? 0) > 2) {
					this.formGroup.get('askslot')?.setValue(this.askslotDayValue);
				}
			}
		}
		this.formGroup.updateValueAndValidity({ emitEvent: true });

		this.is_valid()
	}


	onChangeDeliveryMode(event: MatButtonToggleChange) {
		this.formGroup.get('mode')?.setValue(event.value);
		this.formGroup.get('askdatdel')?.patchValue(new Date(), { emitEvent: false });	// Set askdatdel to today
		this.formGroup.get('askslot')?.setValue(null);
		this.formGroup.get('askop')?.setValue(null);
		this.niveauPrecisionhoraire = 0;
		this.precisionControl2.setValue(false);	// Uncheck precision 2
		this.precisionControl1.setValue(false);	// Uncheck precision 1
		this.is_valid();
	}

	onChangeSlotDay(event: MatButtonToggleChange) {		// Main slot: AM / PM => no precision / no operator
		this.askslotDayValue = event.value;
		this.niveauPrecisionhoraire = 0
		this.precisionControl1.setValue(false);
		this.precisionControl2.setValue(false);
		this.formGroup.get('askop')?.setValue(null);
		this.formGroup.get("askslot")?.patchValue(event.value);
		this.is_valid()
	}

	// Valeur du toggle Matin/Après-midi : '' si pas de créneau, '1'/'2' si créneau jour,
	// sinon déduit du parent du créneau horaire (liste des créneaux du matin).
	getSlotDayValue() {
		const slot = Number(this.formGroup.get('askslot')?.value) || 0;
		if (slot === 0) return '';
		if (slot < 3) return String(slot);
		return this.slotsMorning.find((e) => e.id == slot) != undefined ? '1' : '2';
	}

	is_valid(): boolean {
		this.errorMessage = '';

		if ((this.formGroup.get("mode")?.value ?? 1) < 4) {		// Modes 1 (emporté), 2 (emporté plus tard), 3 (ASAP) => toujours valide
			this.errorMessage = '';
		} else {												// Mode 4 (livraison programmée) => date + créneau (+ précisions) requis
			const askslot = Number(this.formGroup.get('askslot')?.value) || 0;
			const askop = Number(this.formGroup.get('askop')?.value) || 0;
			if (askslot < 1) this.errorMessage += 'Veuillez sélectionner Matin ou Après Midi, ';
			if (this.niveauPrecisionhoraire === 1 && (askop < 1 || askop > 3)) {
				this.errorMessage += 'Veuillez sélectionner Début Milieu Fin ou déselectionner Précision 1';
			}
			if (this.niveauPrecisionhoraire === 2) {
				if (askop < 4) this.errorMessage += 'Veuillez sélectionner Avant Après Horaire Fixe et un horaire ou déselectionner Précision 2 ';
				else if (askslot < 3) this.errorMessage += 'Veuillez sélectionner un horaire ';
			}
		}

		this.formGroup.updateValueAndValidity({ emitEvent: true });
		return this.errorMessage === '';
	}

	ngOnDestroy() {
		this.loadOrderEventsSubscription?.unsubscribe();
		this.formGroupChangeSubscription?.unsubscribe();
	}

	showDatedel() {
		return this.formGroup.get("mode")?.value == 4;
	}

	// --- Temps sur place (delivery.duration, secondes — saisi en minutes) ---

	// Préremplit duration avec le paramètre par défaut UNIQUEMENT si non renseigné.
	// Test « == null » et pas falsy : une duration 0 explicite (dépose sans arrêt) doit être conservée.
	ensureDurationDefault(): void {
		const ctrl = this.formGroup?.get('duration');
		if (!ctrl) return;
		if (ctrl.value === null || ctrl.value === undefined || ctrl.value === '') {
			ctrl.patchValue(this.logisticParams.params().default_duration_s, { emitEvent: false });
		}
	}

	get durationMin(): number | string {
		const v = this.formGroup?.get('duration')?.value;
		return (v === null || v === undefined || v === '') ? '' : Math.round(+v / 60);
	}

	// Pas des flèches haut/bas (minutes) : paramètre de configuration, 5 min en son absence
	get durationStepMin(): number {
		const step = this.logisticParams.params().duration_step_min;
		return step > 0 ? step : 5;
	}

	onDurationMinChange(v: string): void {
		const ctrl = this.formGroup?.get('duration');
		if (!ctrl) return;
		const minutes = v === '' || v === null || isNaN(+v) || +v < 0 ? null : Math.min(Math.round(+v), 999);	// 3 digits max
		ctrl.setValue(minutes === null ? null : minutes * 60);	// minutes -> secondes
		ctrl.markAsDirty();
	}

	// Chiffres uniquement, 3 max : un <input type="number"> laisse passer lettres / e / +- (Firefox les affiche)
	onDurationKeydown(ev: KeyboardEvent): void {
		if (ev.key.length !== 1 || ev.ctrlKey || ev.metaKey) return;		// navigation, Backspace, raccourcis…
		if (!/\d/.test(ev.key)) { ev.preventDefault(); return; }
		const el = ev.target as HTMLInputElement;
		const sel = (el.selectionEnd ?? 0) - (el.selectionStart ?? 0);
		if (el.value.length - sel >= 3) ev.preventDefault();
	}

	// --- Attachments ---
	// L'état vit ici (c'est ce composant qui tient `lnk_attachs`), l'affichage vit dans la
	// page : delivery.component appelle onFileSelected() et lit attachs_images par #plan.

	get deliveryId(): number {
		return this.formGroup.get('id')?.value ?? 0;
	}

	get defaultMemoControl(): FormControl | null {
		const lnkAttachs = this.formGroup.get('lnk_attachs') as FormArray;
		if (!lnkAttachs) return null;
		const index = lnkAttachs.controls.findIndex((ctrl) => {
			const data = ctrl.get('data')?.value;
			return data?.flags?.includes('is_default_memo');
		});
		if (index === -1) return null;
		return lnkAttachs.at(index).get('memo') as FormControl;
	}

	// FormGroup complet de l'attach default_memo (id, objecttyp, objectid, memo…) pour la sauvegarde ciblée
	get defaultMemoAttach(): FormGroup | null {
		const lnkAttachs = this.formGroup.get('lnk_attachs') as FormArray;
		if (!lnkAttachs) return null;
		const index = lnkAttachs.controls.findIndex((ctrl) => {
			const data = ctrl.get('data')?.value;
			return data?.flags?.includes('is_default_memo');
		});
		return index === -1 ? null : (lnkAttachs.at(index) as FormGroup);
	}

	// BL signé : le mémo ne peut plus être sauvé via l'événement livraison (bloqué client + serveur).
	// On le persiste directement sur l'attach (event ATTACH_SAVE_MEMO), au blur, uniquement s'il a changé.
	onMemoBlur(): void {
		if (!this.locked) return;										// hors BL signé : le mémo est sauvé avec la livraison
		const memoControl = this.defaultMemoControl;
		const memoAttach = this.defaultMemoAttach;
		if (!memoControl || !memoAttach || !memoControl.dirty) return;	// rien à sauver si inchangé
		this.attachService.saveMemo$(memoAttach.value).subscribe({
			next: (response: any) => {
				if (response?.data?.id && !memoAttach.get('id')?.value) {
					memoAttach.get('id')?.setValue(response.data.id, { emitEvent: false });	// récupère l'id en cas de création
				}
				memoControl.markAsPristine();
				this.alertService.success("Commentaire enregistré");
			},
			error: (e) => {
				this.alertService.error("ERREUR : le commentaire n'a pas été enregistré");
				console.error(e.error);
			},
		});
	}

	private handleMediaUpload(upload$: Observable<any>): void {
		upload$.subscribe({
			next: (response: any) => {
				if (response?.status === 'placeholder' && response?.item) {
					this.attachs_images.push(response.item);
				} else if (response?.status === 'completed' && response?.item) {
					const idx = this.attachs_images.findIndex(a => a.seq === response.item.seq);
					if (idx !== -1) {
						this.attachs_images[idx] = response.item;
						this.attachService.loadThumbnailImage$(response.item).pipe(take(1)).subscribe(blob => {
							this.attachs_images[idx].thumbnailLocalUrl = URL.createObjectURL(blob);
						});
					}
				}
			},
			error: (error: any) => console.error('Upload failed', error)
		});
	}

	onFileSelected(event: any): void {
		const file: File = event.target.files[0];
		if (!file) return;
		this.handleMediaUpload(
			this.attachService.uploadMediaWithPlaceholder$(file, ObjectType.DELIVERY, this.deliveryId, this.attachs_images)
		);
	}
}
